import { useEffect, useState } from 'react';

import { apiStrapi } from 'services/apiService';
import { useIsMobile } from 'utils/mediaQueries';

// Cache global simples
let cachedData: {
  mediaUrl: string | null;
  mediaType: string | null;
  error: Error | null;
  isLoaded: boolean;
} = {
  mediaUrl: null,
  mediaType: null,
  error: null,
  isLoaded: false,
};

let loadingPromise: Promise<void> | null = null;

export const useDynamicBackground = () => {
  const isMobile = useIsMobile();
  const [mediaUrl, setMediaUrl] = useState<string | null>(cachedData.mediaUrl);
  const [mediaType, setMediaType] = useState<string | null>(cachedData.mediaType);
  const [isLoading, setIsLoading] = useState(!cachedData.isLoaded);
  const [error, setError] = useState<Error | null>(cachedData.error);

  useEffect(() => {
    let isMounted = true;

    // Se já temos dados em cache, sincroniza o estado local
    if (cachedData.isLoaded) {
      setMediaUrl(cachedData.mediaUrl);
      setMediaType(cachedData.mediaType);
      setError(cachedData.error);
      setIsLoading(false);
      return;
    }

    // Se já existe uma promise em andamento, aguarda ela
    if (loadingPromise) {
      loadingPromise.then(() => {
        if (isMounted) {
          setMediaUrl(cachedData.mediaUrl);
          setMediaType(cachedData.mediaType);
          setError(cachedData.error);
          setIsLoading(false);
        }
      });
      return;
    }

    // Faz a requisição e salva no cache
    loadingPromise = (async () => {
      try {
        const response = await apiStrapi.get('/organizations/onboarding-background');
        const data = response.data;
        if (data && data.url && data.mime) {
          let url = data.url;
          let mime = data.mime;
          if (data.formats) {
            let largestFormat = Object.entries(data.formats).reduce(
              (largest, [key, format]) => {
                const currentFormat = format as { sizeInBytes: number; url: string; mime: string };
                if (!largest || currentFormat.sizeInBytes > largest.sizeInBytes) {
                  return { key, ...currentFormat };
                }
                return largest;
              },
              null as { key: string; sizeInBytes: number; url: string; mime: string } | null
            );
            if (largestFormat) {
              url = largestFormat.url;
              mime = largestFormat.mime;
            }
          }
          cachedData = {
            mediaUrl: url,
            mediaType: mime,
            error: null,
            isLoaded: true,
          };
          if (isMounted) {
            setMediaUrl(url);
            setMediaType(mime);
            setError(null);
            setIsLoading(false);
          }
        } else {
          cachedData = {
            mediaUrl: null,
            mediaType: null,
            error: null,
            isLoaded: true,
          };
          if (isMounted) {
            setMediaUrl(null);
            setMediaType(null);
            setError(null);
            setIsLoading(false);
          }
        }
      } catch (apiError) {
        cachedData = {
          mediaUrl: null,
          mediaType: null,
          error: apiError as Error,
          isLoaded: true,
        };
        if (isMounted) {
          setMediaUrl(null);
          setMediaType(null);
          setError(apiError as Error);
          setIsLoading(false);
        }
      } finally {
        loadingPromise = null;
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isMobile]);

  return { mediaUrl, mediaType, isLoading, error };
};
